/**
 * PersonalInboxToolbar — one action bar for the personal inbox.
 *
 * Replaces a row that had grown to eleven always-visible controls: a search
 * box, a separate sender box, two mutually-exclusive filter buttons, two bare
 * date inputs, a sort field select, a sort direction select, a result count,
 * and a refresh icon — sitting under a header that carried a further three
 * sync buttons. Everything was equally loud, so nothing read as primary.
 *
 * The rebuild keeps Compose, the source pills and search on the surface, and
 * ends the bar with the design's four icon buttons — Filter, Sort, Sync,
 * Settings. Sender, dates and status fold into the Filters popover, which
 * reports how many of ITS OWN filters are active.
 *
 * What is NOT in that popover is as deliberate as what is. Source lives on the
 * bar as segmented pills, and sort has its own menu, so neither is duplicated
 * inside — a filter offered in two places is a filter whose two copies
 * disagree about what is selected. There is no "connect another mailbox" `+`
 * either: connecting one is a Settings journey, and the Settings button is the
 * next control along.
 *
 * `InboxFiltersBar` now mirrors this LAYOUT for the unified inbox, which
 * reverses what this note used to say. What the note was protecting is still
 * enforced: the two bars share no state, no credentials and no scope, and each
 * keeps its own filter vocabulary — this one filters on `unread`/`provider` and
 * sorts server-side by `sort_by`, where the unified bar filters on `replied`
 * and sorts by conversation-level keys. Folding those two vocabularies into one
 * component is what the note was warning against, and that has not happened.
 */
import React, { useEffect, useState } from 'react';
import {
    Check,
    Loader2,
    Search,
    Trash2,
    X,
} from 'lucide-react';

import { Button } from '../ui/button';
import SourceChips from './SourceChips';
import {
    ComposeButton,
    InboxCountLabel,
    InboxFilterButton,
    InboxSettingsButton,
    InboxSortMenu,
    SyncButton,
} from './inboxControls';
import { personalSort } from './mailSortOptions';
import { Input } from '../ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';

const STATUSES = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'unreplied', label: 'Needs reply' },
    { key: 'replied', label: 'Replied' },
];

/** Which status pill is lit, derived from the two independent flags. */
function activeStatus(filters) {
    if (filters.unread === true) return 'unread';
    if (filters.replied === false) return 'unreplied';
    if (filters.replied === true) return 'replied';
    return 'all';
}

/** The flags a status pill sets. Only one status is meaningful at a time. */
function statusPatch(key) {
    switch (key) {
        case 'unread':
            return { unread: true, replied: undefined };
        case 'unreplied':
            return { unread: undefined, replied: false };
        case 'replied':
            return { unread: undefined, replied: true };
        default:
            return { unread: undefined, replied: undefined };
    }
}

export default function PersonalInboxToolbar({
    filters,
    onChange,
    resultCount = 0,
    totalCount = 0,
    onSync,
    isSyncing = false,
    canSync = false,
    /** §11.4's control strip: Compose is the strip's violet primary. */
    onCompose,
    /** `{ gmail: 'healthy'|'degraded'|null, outlook: ... }` for the source chips. */
    sources = {},
    onSettings,
    selectedCount = 0,
    onBulkDelete,
    onClearSelection,
    isDeleting = false,
}) {
    const [localSearch, setLocalSearch] = useState(filters.search || '');
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Debounce search — 300ms before propagating, so typing doesn't fire a
    // request (and reset pagination) on every keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== (filters.search || '')) {
                onChange({ ...filters, search: localSearch || undefined });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reflect external resets (the Clear button) back into the input.
    useEffect(() => {
        if ((filters.search || '') !== localSearch) {
            setLocalSearch(filters.search || '');
        }
    }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (patch) => onChange({ ...filters, ...patch });

    // Search and SOURCE are both excluded: each has its own always-visible
    // control on the bar — the box and the segmented pills — so counting
    // either here would show "1 filter" for something the user can already
    // see, next to a popover that does not contain it.
    const activeCount =
        (filters.sender ? 1 : 0) +
        (filters.dateFrom || filters.dateTo ? 1 : 0) +
        (filters.starred ? 1 : 0) +
        (filters.hasAttachment ? 1 : 0) +
        (activeStatus(filters) !== 'all' ? 1 : 0);

    const clearAll = () => {
        setLocalSearch('');
        onChange({
            search: undefined,
            sender: undefined,
            dateFrom: undefined,
            dateTo: undefined,
            replied: undefined,
            unread: undefined,
            starred: undefined,
            hasAttachment: undefined,
            provider: undefined,
            direction: 'desc',
            sortBy: 'comm_date',
        });
    };

    // Selection takes over the bar. Showing filters and a destructive action
    // at once invites deleting against a filter the user has forgotten is on.
    if (selectedCount > 0) {
        return (
            <div
                className="px-4 py-2 border-b border-stone-100 flex items-center gap-3 bg-stone-900"
                data-testid="personal-inbox-bulk-bar"
            >
                <span className="text-sm font-medium text-white">
                    {selectedCount} selected
                </span>
                <Button
                    variant="destructive"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={onBulkDelete}
                    disabled={isDeleting}
                    data-testid="personal-inbox-bulk-delete"
                >
                    {isDeleting ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Delete
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-stone-300 hover:text-white hover:bg-stone-800"
                    onClick={onClearSelection}
                    data-testid="personal-inbox-bulk-cancel"
                >
                    Cancel
                </Button>
            </div>
        );
    }

    return (
        <div
            className="px-4 py-2 border-b border-stone-100 flex items-center gap-2"
            data-testid="personal-inbox-toolbar"
        >
            {onCompose && <ComposeButton onClick={onCompose} />}

            {/* Source is the filter people reach for most on a merged inbox —
                "where did this come from" is the first question two accounts
                raise — so the design gives it a permanent segmented control
                rather than a row inside the Filters popover. */}
            <SourceChips
                value={filters.provider}
                onChange={(provider) => set({ provider })}
                sources={sources}
            />

            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-md">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                <Input
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder="Search mail…"
                    className="pl-8 h-8 text-sm bg-stone-50 border-stone-200"
                    data-testid="personal-inbox-search"
                    aria-label="Search personal inbox"
                />
                {localSearch && (
                    <button
                        type="button"
                        onClick={() => setLocalSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        aria-label="Clear search"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <div className="flex-1" />

            <InboxCountLabel
                shown={resultCount}
                total={totalCount}
                testId="personal-inbox-count"
            />

            {/* Filter · Sort · Sync · Settings — the design's four icon buttons,
                in that order, on both lanes. They were spread across the bar
                with labels: Filters and the sort toggle sat left of the count
                and read as loud as Compose, which is the opposite of what a
                control strip is for. */}
            <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
                <PopoverTrigger asChild>
                    <InboxFilterButton
                        activeCount={activeCount}
                        open={filtersOpen}
                        testId="personal-inbox-filters-trigger"
                    />
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-4 space-y-4 overflow-hidden">
                    {/* Status — one choice, so pills rather than two toggles
                        that could both be pressed into a contradiction. */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Status</p>
                        <div className="flex flex-wrap gap-1.5">
                            {STATUSES.map((s) => {
                                const isActive = activeStatus(filters) === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => set(statusPatch(s.key))}
                                        className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                            isActive
                                                ? 'bg-stone-900 text-white'
                                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                        }`}
                                        data-testid={`personal-inbox-status-${s.key}`}
                                    >
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Starred / Has attachment — §11.4's Filter-by rows.
                        Independent toggles rather than pills: unlike Status,
                        these compose with each other and with everything else.
                        Both map onto real `MailPredicate` fields, so a bulk
                        action over the filtered view acts on exactly these
                        rows. */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Show only</p>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                type="button"
                                onClick={() =>
                                    set({ starred: filters.starred ? undefined : true })
                                }
                                aria-pressed={Boolean(filters.starred)}
                                data-testid="personal-inbox-filter-starred"
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    filters.starred
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                Starred
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    set({
                                        hasAttachment: filters.hasAttachment ? undefined : true,
                                    })
                                }
                                aria-pressed={Boolean(filters.hasAttachment)}
                                data-testid="personal-inbox-filter-attachment"
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    filters.hasAttachment
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                Has attachment
                            </button>
                        </div>
                    </div>

                    {/* Sender */}
                    <div className="space-y-1.5">
                        <label
                            className="text-xs font-medium text-stone-500"
                            htmlFor="personal-inbox-sender"
                        >
                            From
                        </label>
                        <Input
                            id="personal-inbox-sender"
                            value={filters.sender || ''}
                            onChange={(e) => set({ sender: e.target.value || undefined })}
                            placeholder="Name or email…"
                            className="h-8 text-sm bg-stone-50 border-stone-200"
                            data-testid="personal-inbox-sender"
                        />
                    </div>

                    {/* Date range */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Received</p>
                        <div className="flex flex-col gap-1.5">
                            <Input
                                type="date"
                                value={filters.dateFrom || ''}
                                onChange={(e) => set({ dateFrom: e.target.value || undefined })}
                                className="h-8 text-xs bg-stone-50 border-stone-200 w-full"
                                aria-label="Received from"
                                data-testid="personal-inbox-date-from"
                            />
                            <Input
                                type="date"
                                value={filters.dateTo || ''}
                                onChange={(e) => set({ dateTo: e.target.value || undefined })}
                                className="h-8 text-xs bg-stone-50 border-stone-200 w-full"
                                aria-label="Received until"
                                data-testid="personal-inbox-date-to"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-stone-100">
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-xs text-stone-500 hover:text-stone-800"
                            data-testid="personal-inbox-clear-filters"
                        >
                            Clear all
                        </button>
                        <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setFiltersOpen(false)}
                        >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Done
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>

            <InboxSortMenu
                value={personalSort.activeKey(filters)}
                onChange={(key) => set(personalSort.patch(key))}
                testId="personal-inbox-sort"
            />

            {/* One Sync button for every connected mailbox. Previously three
                controls sat here — "Sync Gmail", "Sync Outlook", and a refresh
                icon — which asked the user to know which provider a missing
                message came from before they could go looking for it. */}
            <SyncButton
                onClick={onSync}
                isSyncing={isSyncing}
                canSync={canSync}
                testId="personal-inbox-sync"
            />

            <InboxSettingsButton onClick={onSettings} />
        </div>
    );
}
